/**
 * Qué hacer con lo que dicen los mails, armado con el resumen y el análisis a fondo del período. Pedido de Alan del
 * 12/10/2026: «una página de acciones a realizar… dato → información → acción, que explique qué acciones deberíamos
 * realizar».
 *
 * Cada acción tiene las tres partes:
 * - **Dato**: lo medido, con su número («En Yahoo rebota el 13 % (12 de 92)»).
 * - **Información**: qué quiere decir («son direcciones que ya no existen; cada rebote le baja la reputación a la casilla»).
 * - **Acción**: qué hacer, concreto, y dónde (un link a la lista que hay que mirar; lo que se hace en Snov, sólo para el equipo).
 *
 * Las mismas bases mínimas que las alertas y los hallazgos (50 personas para una tasa de campaña, 30 para un proveedor):
 * una acción sacada de 3 casos hace tomar decisiones con ruido, y es peor que ninguna.
 */

import type { AnalisisEmails, FilaSegmento, ResumenEmails } from "../types";
import { num, plural } from "../../campana-nueva/utils/format";
import { R_EMAILS, tasaTexto } from "./emails";
import { textoDemora } from "./analisis";

export type Prioridad = "alta" | "media" | "baja";

export interface AccionEmails {
  /** estable: sirve de key y para los tests */
  id: string;
  prioridad: Prioridad;
  /** la acción en una frase */
  titulo: string;
  dato: string;
  informacion: string;
  accion: string;
  enlace?: { texto: string; a: string };
  /** lo que se hace en la cuenta de Snov de Deenex: sólo lo ve el equipo */
  equipo?: string;
}

/** Las de las alertas de campaña (`emails.util.ts` del backend) y las del análisis (`analisis.util.ts`). */
export const BASE_CAMPANA = 50;
export const BASE_SEGMENTO = 30;
export const REBOTE_AMBAR = 0.03;
export const REBOTE_ROJO = 0.05;
export const REBOTE_PROVEEDOR = 0.05;
export const BAJAS_ALTAS = 0.005;
export const APERTURA_BAJA = 0.15;
export const AUTOMATICAS_ALTAS = 0.1;
export const INTERACCIONES_MOMENTO = 20;

const ORDEN: Record<Prioridad, number> = { alta: 0, media: 1, baja: 2 };
const DIAS = ["lunes", "martes", "miércoles", "jueves", "viernes", "sábados", "domingos"];
const hh = (h: number) => `${String(h % 24).padStart(2, "0")}:00`;
const personas = (filtro: string) => `${R_EMAILS}/personas?filtro=${filtro}`;

/** Los proveedores donde casi nadie abre contra el total (ahí el mail cae en spam), con la misma regla del hallazgo. */
function proveedoresEnSpam(proveedores: readonly FilaSegmento[]): { p: FilaSegmento; general: number }[] {
  const conBase = proveedores.filter((p) => p.tasas.apertura !== null && p.entregados >= BASE_SEGMENTO);
  const abrieron = conBase.reduce((s, p) => s + (p.abrieron || 0), 0);
  const base = conBase.reduce((s, p) => s + p.entregados, 0);
  const general = base ? abrieron / base : 0;
  if (general <= 0) return [];
  // iCloud no: el iPhone abre solo, su tasa no dice nada de la bandeja
  return conBase.filter((p) => p.clave !== "apple" && (p.tasas.apertura as number) < general * 0.5).map((p) => ({ p, general }));
}

export function accionesEmails(r: ResumenEmails, a: AnalisisEmails | null): AccionEmails[] {
  const out: AccionEmails[] = [];
  const k = r.conteos;

  // 1. Quienes pidieron la baja contestando y siguen en la lista: lo primero, porque es un reclamo posible.
  if (r.bajasPendientes > 0) {
    const n = r.bajasPendientes;
    out.push({
      id: "bajas-pendientes",
      prioridad: "alta",
      titulo: "Sacá de la lista a quienes pidieron la baja",
      dato: `${num(n)} ${plural(n, "persona contestó", "personas contestaron")} pidiendo la baja y ${plural(n, "sigue", "siguen")} en la lista.`,
      informacion:
        "Snov no las saca solas: la próxima campaña les vuelve a llegar. Para esa persona es spam, lo marca como tal y la casilla pierde reputación; además, no respetar una baja es motivo de reclamo.",
      accion: "Pasalas a «No enviar» con el aviso de arriba del Resumen: es un clic y no les llega ninguna campaña más.",
      enlace: { texto: "Ir al Resumen", a: R_EMAILS },
    });
  }

  // 2. Campañas que siguen mandando sin la baja en un clic: una sola acción, aunque sean varias (en HATSU eran 3
  //    tarjetas iguales).
  const sinBaja = r.alertas.filter((x) => x.codigo === "sin_baja_un_clic");
  if (sinBaja.length) {
    const [al] = sinBaja;
    const varias = sinBaja.length > 1;
    const nombres = sinBaja.map((x) => `«${x.campana}»`);
    out.push({
      id: "sin-baja-un-clic",
      prioridad: "alta",
      titulo: varias ? `Agregá la baja en un clic a ${num(sinBaja.length)} campañas` : `Agregá la baja en un clic a «${al.campana}»`,
      dato: varias
        ? `${nombres.slice(0, -1).join(", ")} y ${nombres[nombres.length - 1]} siguen mandando sin el link para darse de baja.`
        : "La campaña sigue mandando y el mail no lleva el link para darse de baja.",
      informacion: al.detalle,
      accion: varias ? "Prendé el link de baja en cada una antes de su próximo envío." : "Prendé el link de baja antes del próximo envío de esa campaña.",
      enlace: varias ? { texto: "Ver las campañas", a: `${R_EMAILS}/campanas` } : { texto: "Ver la campaña", a: `${R_EMAILS}/campanas/${al.campanaId}` },
      ...(al.equipo ? { equipo: al.equipo } : {}),
    });
  }

  // 3. Rebotes: por proveedor si alguno se destaca; si no, el total.
  const rebotaProveedor = (a?.proveedores || []).filter((p) => p.contactados >= BASE_SEGMENTO && (p.tasas.rebote || 0) >= REBOTE_PROVEEDOR);
  for (const p of rebotaProveedor) {
    const t = p.tasas.rebote as number;
    out.push({
      id: `rebote-${p.clave}`,
      prioridad: t >= 0.1 ? "alta" : "media",
      titulo: `Limpiá las direcciones de ${p.nombre} que rebotan`,
      dato: `En ${p.nombre} rebota el ${tasaTexto(t)} (${num(p.rebotes)} de ${num(p.contactados)}).`,
      informacion: `Son direcciones que ya no existen o están mal escritas. Cada rebote le baja la reputación a la casilla que manda, y con la reputación baja los mails caen en spam en todos lados, no sólo en ${p.nombre}.`,
      accion: "Antes de la próxima campaña, sacá de la lista las direcciones que rebotaron y no las vuelvas a cargar.",
      enlace: { texto: "Ver las que rebotaron", a: personas("rebotaron") },
      equipo: "En Snov, pasalas a la lista de «No enviar» para que no vuelvan a entrar con otra lista.",
    });
  }
  if (!rebotaProveedor.length && k.contactados >= BASE_CAMPANA && k.rebotes / k.contactados >= REBOTE_AMBAR) {
    const t = k.rebotes / k.contactados;
    out.push({
      id: "rebote-total",
      prioridad: t >= REBOTE_ROJO ? "alta" : "media",
      titulo: "Limpiá la lista: rebotan demasiados mails",
      dato: `Rebotó el ${tasaTexto(t)} de los mails (${num(k.rebotes)} de ${num(k.contactados)}).`,
      informacion: "La lista tiene direcciones viejas o mal escritas. Desde el 5 % la casilla corre riesgo de terminar en spam.",
      accion: "Sacá las direcciones que rebotaron antes de volver a mandar.",
      enlace: { texto: "Ver las que rebotaron", a: personas("rebotaron") },
    });
  }

  // 4. Una casilla que rebota de más.
  for (const c of (a?.remitentes || []).filter((x) => x.contactados >= BASE_CAMPANA && (x.tasas.rebote || 0) >= REBOTE_ROJO)) {
    out.push({
      id: `rebote-casilla-${c.clave}`,
      prioridad: "alta",
      titulo: `Frená la casilla ${c.nombre} hasta limpiar su lista`,
      dato: `La casilla ${c.nombre} rebota el ${tasaTexto(c.tasas.rebote)} (${num(c.rebotes)} de ${num(c.contactados)}).`,
      informacion: "Gmail y Outlook miran cuánto rebota cada casilla: con tanto rebote, lo próximo que mande va a spam aunque la lista esté limpia.",
      accion: "No mandes más desde esa casilla hasta sacar las direcciones que rebotaron.",
      enlace: { texto: "Ver las que rebotaron", a: personas("rebotaron") },
      equipo: "En Snov, pausá las campañas que salen de esa casilla y mirá su calentamiento en Conexión.",
    });
  }

  // 5. Un proveedor donde casi nadie abre: spam en ese proveedor.
  for (const { p, general } of proveedoresEnSpam(a?.proveedores || [])) {
    out.push({
      id: `spam-${p.clave}`,
      prioridad: "alta",
      titulo: `Revisá por qué no llegan a la bandeja de ${p.nombre}`,
      dato: `En ${p.nombre} abre el ${tasaTexto(p.tasas.apertura)}, contra el ${tasaTexto(general)} del total.`,
      informacion: "Que en un solo proveedor casi nadie abra no es desinterés de la gente: es que ahí el mail llega a spam o a «Promociones».",
      accion: `Mandá a esas direcciones de a poco y con un mail simple: sin links acortados, con pocas imágenes y un asunto sin mayúsculas. Antes, mandá una prueba a una casilla de ${p.nombre} y fijate dónde cae.`,
      equipo: "Revisá que el dominio de la casilla tenga SPF, DKIM y DMARC, y que su calentamiento en Snov esté al día.",
    });
  }

  // 6. Las personas que mostraron interés: la oportunidad de venta.
  const interesadas = a?.interesadas || [];
  if (interesadas.length > 0) {
    const n = interesadas.length;
    const conTelefono = interesadas.filter((x) => (x.telefono || "").trim()).length;
    const demora = a?.demoras.respuesta;
    const rapido = demora && demora.personas >= 10 && demora.medianaMin !== null ? ` La mitad de las respuestas llega en menos de ${textoDemora(demora.medianaMin)}: el interés dura poco.` : "";
    out.push({
      id: "interesadas",
      prioridad: "alta",
      titulo: "Contactá a quienes mostraron interés",
      // la lista del análisis tiene tope (las 25 más interesadas): se dice así, no como el total
      dato: n === 1
        ? `1 persona hizo clic o contestó${conTelefono ? " (con teléfono)" : ""}.`
        : `Las ${num(n)} personas más interesadas hicieron clic o contestaron${conTelefono ? ` (${num(conTelefono)} con teléfono)` : ""}.`,
      informacion: `Son las más calientes de la lista: ya dieron una señal de interés.${rapido}`,
      accion: "Escribiles por WhatsApp o llamalas esta semana, con una oferta concreta. No esperes a la próxima campaña.",
      enlace: { texto: "Ver quiénes son", a: personas(k.respondieron > 0 ? "respondieron" : "clic") },
    });
  }

  // 7. Los «quizás» de Snov.
  if (a?.interes && a.interes.quizas > 0) {
    const n = a.interes.quizas;
    out.push({
      id: "quizas",
      prioridad: "media",
      titulo: "Volvé a escribirles a quienes dijeron «quizás»",
      dato: `Snov clasificó ${num(n)} ${plural(n, "respuesta", "respuestas")} como «quizás».`,
      informacion: "No dijeron que no: les falta un motivo o el momento.",
      accion: "Mandales un segundo mail con algo concreto (una fecha, un beneficio, un cupo) en vez de repetir el mismo.",
      enlace: { texto: "Ver las respuestas", a: `${R_EMAILS}/respuestas` },
    });
  }

  // 8. Bajas altas.
  const bajas = k.bajas + (k.pidieronBaja || 0);
  if (k.entregados >= BASE_CAMPANA && bajas / k.entregados >= BAJAS_ALTAS) {
    out.push({
      id: "bajas-altas",
      prioridad: "media",
      titulo: "Revisá a quién le escribís y cada cuánto",
      dato: `Pidió la baja el ${tasaTexto(bajas / k.entregados)} (${num(bajas)} de ${num(k.entregados)}).`,
      informacion: "Para un comercio que le escribe a sus clientes es alto: o la lista tiene gente que no te conoce, o le llegan demasiados mails.",
      accion: "Mandá sólo a quienes compraron o se anotaron, y espaciá los envíos a la misma persona.",
      enlace: { texto: "Ver quiénes se dieron de baja", a: personas("bajas") },
    });
  }

  // 9. Apertura baja (con la de Snov, que encima viene inflada por lo automático).
  if (k.abrieron !== null && k.entregados >= BASE_CAMPANA && k.abrieron / k.entregados < APERTURA_BAJA) {
    out.push({
      id: "apertura-baja",
      prioridad: "media",
      titulo: "Probá otro asunto",
      dato: `Abrió el ${tasaTexto(k.abrieron / k.entregados)} de quienes recibieron el mail.`,
      informacion: "Lo primero que se ve es el asunto y quién lo manda: si no abren, el problema está ahí (o el mail llega a spam: mirá los rebotes y los proveedores).",
      accion: "En la próxima, mandá dos asuntos distintos a dos mitades de la lista y quedate con el que más abran. Que el remitente tenga el nombre del comercio.",
    });
  }

  // 10. El mejor momento (con el sesgo de cuándo se mandó).
  const mapa = a?.mapa || [];
  if (mapa.reduce((s, c) => s + c.clics + c.respuestas, 0) >= INTERACCIONES_MOMENTO) {
    const mejor = [...mapa].sort((x, y) => y.clics + y.respuestas - (x.clics + x.respuestas) || y.aperturas - x.aperturas)[0];
    const total = mapa.reduce((s, c) => s + (c.envios || 0), 0);
    const ese = mapa.filter((c) => c.dia === mejor.dia).reduce((s, c) => s + (c.envios || 0), 0);
    const sesgo = total > 0 && ese / total >= 0.5;
    out.push({
      id: "mejor-momento",
      prioridad: "media",
      titulo: sesgo ? "Probá mandar otro día y a otra hora" : `Mandá los ${DIAS[mejor.dia]} a las ${hh(mejor.hora)}`,
      dato: `Los clics y las respuestas se juntan los ${DIAS[mejor.dia]} entre las ${hh(mejor.hora)} y las ${hh(mejor.hora + 1)}.`,
      informacion: sesgo
        ? `Pero ese día salió el ${tasaTexto(ese / total)} de los mails: puede ser sólo porque se mandó ese día.`
        : "Es cuando tu gente está mirando el correo: un mail que llega ahí queda arriba de la bandeja.",
      accion: sesgo
        ? "Para saberlo de verdad, mandá la próxima campaña en dos tandas (otro día y otra hora) y compará clics y respuestas."
        : "Programá la próxima campaña para ese día, un rato antes de esa hora.",
    });
  }

  // 11. Aperturas automáticas: cambiar la vara con que se mide.
  const au = a?.automaticas;
  if (au && au.aperturasTotales >= BASE_SEGMENTO && au.aperturas / au.aperturasTotales >= AUTOMATICAS_ALTAS) {
    out.push({
      id: "automaticas",
      prioridad: "baja",
      titulo: "Medí las campañas por clics y respuestas, no por aperturas",
      dato: `El ${tasaTexto(au.aperturas / au.aperturasTotales)} de las aperturas llegó a menos de un minuto del envío (${num(au.aperturas)} de ${num(au.aperturasTotales)}).`,
      informacion: "Esas aperturas las hace un sistema (los filtros de seguridad o el correo que precarga el mail), no la persona: la tasa de apertura que muestra Snov queda inflada.",
      accion: "Para decidir si una campaña funcionó, mirá los clics y las respuestas. Las tasas del análisis ya descuentan lo automático.",
    });
  }

  // 12. Campañas que no miden clics.
  if (k.campanas > 0 && k.campanasSinClics > 0) {
    const n = k.campanasSinClics;
    out.push({
      id: "sin-clics",
      prioridad: "baja",
      titulo: "Prendé la medición de clics",
      dato: `${num(n)} de ${num(k.campanas)} ${plural(k.campanas, "campaña", "campañas")} no ${plural(n, "mide", "miden")} los clics.`,
      informacion: "El clic es la señal más confiable de interés (las aperturas pueden ser automáticas): sin él no se sabe quién se interesó.",
      accion: "Prendé el seguimiento de clics en las próximas campañas.",
      equipo: "Se prende en Snov, en la configuración del envío de cada campaña.",
    });
  }

  return out.map((x, i) => ({ x, i })).sort((p, q) => ORDEN[p.x.prioridad] - ORDEN[q.x.prioridad] || p.i - q.i).map(({ x }) => x);
}
